import { Component, Input, Type, ViewChildren, QueryList, OnDestroy, inject, AfterViewInit, effect } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { MenuItem } from 'primeng/api';

import { DCFilterBarComponent, EntityBaseListComponent, ICustomFilter, OnActionEvent, PColumn, QuickTableComponent } from '@dataclouder/ngx-core';
import { ILesson, LESSONS_TOKEN } from '../../lesson-mini-components/components/lessons.clases';

import { DcLessonCardComponent } from '../dc-lesson-card/dc-lesson-card.component';
import { PaginatorModule } from 'primeng/paginator';
import { DefaultLessonsService } from '../../../services/default-lessons.service';

const tableViewColumns: PColumn[] = [
  { field: 'media.images[0].url', header: 'Image', type: 'image' },
  { field: 'title', header: 'Título' },
  { field: 'description', header: 'Descripción' },
];

@Component({
  selector: 'dc-lesson-list',
  templateUrl: './dc-lesson-list.component.html',
  styleUrls: ['./dc-lesson-list.component.scss'],
  standalone: true,
  imports: [RouterModule, DCFilterBarComponent, NgComponentOutlet, QuickTableComponent, PaginatorModule],
})
export class DCLessonListComponent extends EntityBaseListComponent<ILesson> implements OnDestroy, AfterViewInit {
  // Inputs
  @Input() customCardComponent?: Type<DcLessonCardComponent>;
  @Input() customFilters: ICustomFilter[] = [];

  // Injected Services
  // I dont rename so use the same in EntityBaseListComponent and Pagination
  protected override entityCommunicationService = inject(LESSONS_TOKEN, { optional: true }) ?? inject(DefaultLessonsService);

  // View Children
  @ViewChildren('outlet') outlets!: QueryList<NgComponentOutlet>;

  // Public properties
  public cardComponent: Type<DcLessonCardComponent>;
  public override columns: PColumn[] = tableViewColumns;

  // Private properties
  private cardEventSubs: Subscription[] = [];

  constructor() {
    super();
    this.filterConfig.returnProps = {
      title: 1,
      description: 1,
      media: 1,
      _id: 1,
      id: 1,
    };

    effect(() => {
      // When items signal changes, re-subscribe to card events
      this.items(); // a little hack to make the effect run when items change
      setTimeout(() => this.subscribeToCardEvents());
    });
  }

  override async ngOnInit(): Promise<void> {
    this.cardComponent = this.customCardComponent || DcLessonCardComponent;
    await super.ngOnInit();
  }

  ngAfterViewInit(): void {
    this.outlets.changes.subscribe(() => this.subscribeToCardEvents());
  }

  ngOnDestroy() {
    this.clearCardEventSubs();
  }

  public getCustomButtons(item: ILesson): MenuItem[] {
    return [
      { label: 'Select', icon: 'pi pi-check', command: () => this.handleAction({ action: 'select', item }) },
      { label: 'QR', icon: 'pi pi-qrcode', command: () => this.handleAction({ action: 'qr', item }) },
    ];
  }

  public handleAction(actionEvent: OnActionEvent): void {
    if (['select', 'qr'].includes(actionEvent.action)) {
      this.onAction.emit(actionEvent);
    } else {
      super.doAction(actionEvent);
    }
  }

  public applyFilterBarEvent(filterEvent: OnActionEvent): void {
    if (filterEvent.action === 'changeView') {
      this.toggleView();
      return;
    }
    this.filterConfig = { ...this.filterConfig, ...filterEvent.item };
    this.loadData();
  }

  private subscribeToCardEvents(): void {
    this.clearCardEventSubs();
    this.outlets.forEach((outlet) => {
      const instance = outlet.componentInstance as DcLessonCardComponent;
      if (instance?.onAction) {
        this.cardEventSubs.push(
          instance.onAction.subscribe((event: OnActionEvent) => {
            this.handleAction(event);
          }),
        );
      }
    });
  }

  private clearCardEventSubs(): void {
    this.cardEventSubs.forEach((sub) => sub.unsubscribe());
    this.cardEventSubs = [];
  }

  protected override async loadData(): Promise<void> {
    try {
      this.isLoading = true;
      const response = await this.entityCommunicationService.getLessons(this.filterConfig);
      this.items.set(response.rows);
      this.totalRecordsSignal.set(response.count);
    } catch (error) {
      console.error('Error loading data', error);
    } finally {
      this.isLoading = false;
    }
  }
}
